import React, { Suspense, FunctionComponent, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { ThemeProvider, CssBaseline, Box } from "@mui/material";
import { InteractionStatus, InteractionType } from "@azure/msal-browser";
import { primaryTheme } from "./theme/primary.theme";
import { loginRequest } from "./config/auth.config";
import ErrorBoundary from "./components/Common/ErrorBoundary";
import Header from "./components/Common/Header.Component";
import FullpageSpinner from "./components/Common/FullpageSpinner";
import Footer from "./components/Common/Footer.Component";
import ProtectedRoute, { UserType } from "./components/Common/ProtectedRoute";

/* Containers */
const VehiclesContainer = React.lazy(
  () => import("./containers/Vehicles.Container")
);
const UnitsContainer = React.lazy(() => import("./containers/Units.Container"));
const VehicleDetailContainer = React.lazy(
  () => import("./containers/VehicleDetail.Container")
);
const SoftwareContainer = React.lazy(
  () => import("./containers/Software.Container")
);
const AdminContainer = React.lazy(() => import("./containers/Admin.Container"));
const UnitDetailContainer = React.lazy(
  () => import("./containers/UnitDetail.Container")
);
const ActivateContainer = React.lazy(
  () => import("./containers/Activate.Container")
);
const RegisterContainer = React.lazy(
  () => import("./containers/Register.Container")
);
const AccessDeniedContainer = React.lazy(
  () => import("./containers/AccessDenied.Container")
);

const UserTypeRouter: FunctionComponent = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (
      accounts.length > 0 &&
      (location.pathname === "/" || location.pathname === "/Vehicles")
    ) {
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        })
        .then((response) => {
          const claims = response.account.idTokenClaims as any;
          const accessTokenClaims = response.idTokenClaims as any;

          // Support both B2C and Entra ID claim formats
          const userEnum =
            accessTokenClaims?.extension_UserEnum ||
            accessTokenClaims?.UserEnum ||
            accessTokenClaims?.UserType ||
            claims?.extension_UserEnum ||
            claims?.UserEnum ||
            claims?.UserType ||
            claims?.roles?.[0] ||
            accessTokenClaims?.roles?.[0];

          let userType: UserType = UserType.UNDEFINED;

          if (typeof userEnum === "number") {
            userType = userEnum as UserType;
          } else if (typeof userEnum === "string") {
            switch (userEnum.toUpperCase()) {
              case "STANDBYUSER":
              case "1":
                userType = UserType.STANDBYUSER;
                break;
              case "UPFITTER":
              case "2":
                userType = UserType.UPFITTER;
                break;
              case "ENDUSER":
              case "3":
                userType = UserType.ENDCUSTOMER;
                break;
              case "GUEST":
              case "0":
                userType = UserType.GUEST;
                break;
            }
          }

          switch (userType) {
            case UserType.GUEST || UserType.UNDEFINED:
              navigate("/AccessDenied", { replace: true });
              break;
            case UserType.UPFITTER:
              navigate("/Activate", { replace: true });
              break;
            case UserType.ENDCUSTOMER:
              navigate("/Register", { replace: true });
              break;
            // STANDBYUSER stays on default route (Vehicles)
          }
        })
        .catch(async (e) => {
          console.log("Token acquisition failed:", e);
        });
    }
  }, [accounts, instance, location.pathname, navigate]);

  return null;
};

const App: FunctionComponent = () => {
  // I18n
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        margin: 0,
        display: "grid",
        gridTemplateRows: "auto 1fr auto",
      }}
    >
      <ThemeProvider theme={primaryTheme}>
        <CssBaseline />
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={loginRequest}
        >
          <ErrorBoundary>
            <Router>
              <UserTypeRouter />
              <Header organizationName="" />
              <Suspense fallback={<FullpageSpinner text={t("loadingPage")} />}>
                <Routes>
                  <Route path="/" element={<VehiclesContainer />} />
                  <Route
                    path="/Admin"
                    element={
                      <ProtectedRoute allowedRoles={[UserType.STANDBYUSER]}>
                        <AdminContainer />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/AccessDenied"
                    element={<AccessDeniedContainer />}
                  />
                  <Route
                    path="/Vehicles"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          UserType.STANDBYUSER,
                          UserType.UPFITTER,
                          UserType.ENDCUSTOMER,
                        ]}
                      >
                        <VehiclesContainer />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/Units"
                    element={
                      <ProtectedRoute allowedRoles={[UserType.STANDBYUSER]}>
                        <UnitsContainer />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/Software"
                    element={
                      <ProtectedRoute allowedRoles={[UserType.STANDBYUSER]}>
                        <SoftwareContainer />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/Activate"
                    element={
                      <ProtectedRoute
                        allowedRoles={[UserType.UPFITTER, UserType.STANDBYUSER]}
                      >
                        <ActivateContainer />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/Register"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          UserType.ENDCUSTOMER,
                          UserType.STANDBYUSER,
                        ]}
                      >
                        <RegisterContainer />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="Unit">
                    <Route
                      path=":unitId"
                      element={
                        <ProtectedRoute allowedRoles={[UserType.STANDBYUSER]}>
                          <UnitDetailContainer />
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                  <Route path="Vehicle">
                    <Route
                      path=":vehicleId"
                      element={
                        <ProtectedRoute
                          allowedRoles={[
                            UserType.STANDBYUSER,
                            UserType.UPFITTER,
                            UserType.ENDCUSTOMER,
                          ]}
                        >
                          <VehicleDetailContainer />
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                </Routes>
              </Suspense>
              <Footer />
            </Router>
          </ErrorBoundary>
        </MsalAuthenticationTemplate>
      </ThemeProvider>
    </Box>
  );
};

export default App;
